import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Firebase } from '../../firebase/config';
import Arrow from '../../assets/Arrow';
import './LanguageSelector.css';

// All available languages (filtered by admin settings at runtime)
const ALL_LANGUAGES = [
  { code: 'en', name: 'English', shortName: 'EN' },
  { code: 'fr', name: 'Français', shortName: 'FR' },
  { code: 'de', name: 'Deutsch', shortName: 'DE' },
  { code: 'es', name: 'Español', shortName: 'ES' },
  { code: 'it', name: 'Italiano', shortName: 'IT' },
  { code: 'bg', name: 'Български', shortName: 'BG' },
  { code: 'ru', name: 'Русский', shortName: 'RU' },
  { code: 'uk', name: 'Українська', shortName: 'UK' },
  { code: 'be', name: 'Беларуская', shortName: 'BE' },
  { code: 'zh', name: '中文', shortName: 'ZH' },
  { code: 'fa', name: 'فارسی', shortName: 'FA' },
];

/**
 * LanguageSelector Component
 * A reusable component for switching between supported languages.
 * Displays current language and opens a dropdown with all available languages.
 */
export default function LanguageSelector() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [languages, setLanguages] = useState(ALL_LANGUAGES);
  const dropdownRef = useRef(null);

  // Load enabled languages from Firestore admin settings
  useEffect(() => {
    try {
      Firebase.firestore()
        .doc('appSettings/languages')
        .get()
        .then((doc) => {
          if (!doc.exists) return;
          const settings = doc.data();
          const enabled = ALL_LANGUAGES.filter(
            (l) => settings[l.code] !== false || l.code === 'en'
          );
          if (enabled.length > 0) setLanguages(enabled);
        })
        .catch(() => {});
    } catch { /* Firebase not initialized in tests */ }
  }, []);

  // Get current language info
  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  // Handle language change
  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="languageSelector" ref={dropdownRef}>
      <button
        className="languageSelectorButton"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label={`Current language: ${currentLanguage.name}. Click to change.`}
      >
        <span className="languageSelectorCurrent">{currentLanguage.shortName}</span>
        <Arrow className={`languageSelectorArrow ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="languageSelectorDropdown">
          {languages.map((language) => (
            <button
              key={language.code}
              className={`languageSelectorOption ${
                language.code === currentLanguage.code ? 'selected' : ''
              }`}
              onClick={() => handleLanguageChange(language.code)}
            >
              {language.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
